import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, Send, Loader, Copy, Check, Download, FileText, Calculator,
  Package, Lightbulb, MessageSquare, History, Star, Trash2, 
  ChevronDown, ChevronUp, RotateCcw, Info, Building2, Wrench,
  Sparkles, TrendingUp, Layers, DollarSign, Clock, User,
  HelpCircle, Mail, BookOpen, Palette, AlertCircle, Settings
} from 'lucide-react';
import { PageWrapper, Card, SectionTitle } from '@/components/ui/PageWrapper';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokens?: number;
}

interface QuickPrompt {
  id: string;
  category: string;
  title: string;
  prompt: string;
  icon: React.ReactNode;
}

interface SavedConversation {
  id: string;
  title: string;
  messages: Message[];
  timestamp: Date;
  category: string;
}

const quickPrompts: QuickPrompt[] = [
  // Asystent Sprzedaży
  {
    id: 'product-desc',
    category: 'sales',
    title: 'Opisz produkt',
    prompt: 'Napisz profesjonalny opis produktu: ekspozytor z plexi transparentnej 5mm, wymiary 60x40x150cm, 4 półki, do ekspozycji kosmetyków.',
    icon: <FileText className="w-5 h-5" />
  },
  {
    id: 'offer-email',
    category: 'sales',
    title: 'Stwórz ofertę mailową',
    prompt: 'Napisz profesjonalnego maila z ofertą na ekspozytory do apteki. Klient potrzebuje 5 sztuk, budżet około 10 000 zł.',
    icon: <Mail className="w-5 h-5" />
  },
  {
    id: 'client-response',
    category: 'sales',
    title: 'Odpowiedz na zapytanie',
    prompt: 'Klient pyta: "Czy robicie ekspozytory na zamówienie? Potrzebuję czegoś na słodycze do sklepu." Napisz wyczerpującą odpowiedź.',
    icon: <MessageSquare className="w-5 h-5" />
  },
  
  // Doradca Techniczny
  {
    id: 'material-selection',
    category: 'technical',
    title: 'Dobierz materiał',
    prompt: 'Jaki materiał polecasz na ekspozytor zewnętrzny, który będzie wystawiony na działanie słońca i deszczu?',
    icon: <Layers className="w-5 h-5" />
  },
  {
    id: 'problem-solving',
    category: 'technical',
    title: 'Rozwiąż problem',
    prompt: 'Plexi pęka podczas gięcia na gorąco. Jakie mogą być przyczyny i jak temu zapobiec?',
    icon: <Wrench className="w-5 h-5" />
  },
  {
    id: 'mounting-advice',
    category: 'technical',
    title: 'Porada montażowa',
    prompt: 'Jak najlepiej zamocować ekspozytor o wadze 15kg na ścianie z płyt gipsowo-kartonowych?',
    icon: <HelpCircle className="w-5 h-5" />
  },
  
  // Kalkulator Inteligentny
  {
    id: 'estimate-from-desc',
    category: 'calculator',
    title: 'Wycena z opisu',
    prompt: 'Klient potrzebuje: "Stojak na ulotki A4, około 5 kieszeni, przezroczysty, stabilny". Zaproponuj rozwiązanie z wyceną.',
    icon: <Calculator className="w-5 h-5" />
  },
  {
    id: 'cost-optimization',
    category: 'calculator',
    title: 'Optymalizuj koszty',
    prompt: 'Mam projekt ekspozytora za 3500 zł. Jak mogę obniżyć koszty o 20% zachowując funkcjonalność?',
    icon: <DollarSign className="w-5 h-5" />
  },
  {
    id: 'alternatives',
    category: 'calculator',
    title: 'Alternatywne rozwiązania',
    prompt: 'Zaproponuj 3 różne rozwiązania (budżetowe, średnie, premium) na ekspozytor do biżuterii.',
    icon: <Sparkles className="w-5 h-5" />
  },
  
  // Analiza Biznesowa
  {
    id: 'trends',
    category: 'business',
    title: 'Trendy branżowe',
    prompt: 'Jakie są najnowsze trendy w ekspozytorach dla branży kosmetycznej w 2024?',
    icon: <TrendingUp className="w-5 h-5" />
  },
  {
    id: 'competition',
    category: 'business',
    title: 'Analiza konkurencji',
    prompt: 'Jak wyróżnić naszą ofertę ekspozytorów na tle konkurencji? Co możemy zaoferować unikalnego?',
    icon: <Building2 className="w-5 h-5" />
  },
  
  // Asystent Projektowy
  {
    id: 'creative-concept',
    category: 'design',
    title: 'Koncepcja kreatywna',
    prompt: 'Zaprojektuj innowacyjny ekspozytor na perfumy dla luksusowej marki. Brief: elegancja, minimalizm, podświetlenie.',
    icon: <Palette className="w-5 h-5" />
  },
  {
    id: 'assembly-instructions',
    category: 'design',
    title: 'Instrukcja montażu',
    prompt: 'Napisz prostą instrukcję montażu ekspozytora modułowego dla klienta (5 elementów, montaż bez narzędzi).',
    icon: <BookOpen className="w-5 h-5" />
  }
];

// Określ URL backendu - Universal AI Service
const API_URL = import.meta.env.PROD 
  ? 'https://api.plexisystem.pl' 
  : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

export function AIAssistant() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: `Cześć! Jestem asystentem AI PlexiSystem. Mogę pomóc Ci w:

• 📝 Tworzeniu opisów produktów i ofert
• 🔧 Doradztwie technicznym i doborze materiałów  
• 💰 Wycenach i optymalizacji kosztów
• 💡 Generowaniu kreatywnych pomysłów
• 📊 Analizie trendów i konkurencji

Zadaj mi pytanie lub wybierz jeden z gotowych promptów po lewej stronie.`,
      timestamp: new Date()
    }
  ]);
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [savedConversations, setSavedConversations] = useState<SavedConversation[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({ used: 0, limit: 100000 });
  const [dailyUsage, setDailyUsage] = useState({ used: 0, limit: 50000, resetAt: new Date().setHours(24,0,0,0) });
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [lastMessageTime, setLastMessageTime] = useState<number>(0);
  const [messageCount, setMessageCount] = useState({ count: 0, resetAt: Date.now() + 60000 });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load saved conversations from localStorage
    const saved = localStorage.getItem('plexisystem_ai_conversations');
    if (saved) {
      setSavedConversations(JSON.parse(saved));
    }
    
    // Load saved token usage
    const savedUsage = localStorage.getItem('plexisystem_token_usage');
    if (savedUsage) {
      try {
        const usage = JSON.parse(savedUsage);
        const lastUpdate = new Date(usage.lastUpdate);
        const today = new Date();
        
        // Jeśli to ten sam dzień, załaduj zapisane wartości
        if (lastUpdate.toDateString() === today.toDateString()) {
          setTokenUsage(prev => ({ ...prev, used: usage.total || 0 }));
          setDailyUsage(prev => ({ ...prev, used: usage.daily || 0 }));
        } else {
          // Nowy dzień - resetuj dzienny licznik
          setTokenUsage(prev => ({ ...prev, used: usage.total || 0 }));
          setDailyUsage(prev => ({ ...prev, used: 0 }));
        }
      } catch (e) {
        console.error('Error loading token usage:', e);
      }
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageContent?: string) => {
    const content = messageContent || input.trim();
    if (!content || isLoading) return;
    
    // Rate limiting - max 10 wiadomości na minutę
    const now = Date.now();
    
    // Reset licznika co minutę
    if (now > messageCount.resetAt) {
      setMessageCount({ count: 1, resetAt: now + 60000 });
    } else if (messageCount.count >= 10) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '⚠️ Zbyt wiele zapytań! Maksymalnie 10 wiadomości na minutę. Poczekaj chwilę przed wysłaniem kolejnej.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    } else {
      setMessageCount(prev => ({ ...prev, count: prev.count + 1 }));
    }
    
    // Minimalna przerwa między wiadomościami (2 sekundy)
    if (now - lastMessageTime < 2000) {
      return;
    }
    setLastMessageTime(now);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Prepare system context
      const systemPrompt = `Jesteś asystentem AI dla PlexiSystem - firmy produkującej ekspozytory, kasetony, gabloty i inne produkty z plexi i tworzyw sztucznych na zamówienie.

Twoja wiedza obejmuje:
- Materiały: plexi (PMMA), dibond, PCV, poliwęglan, MDF
- Technologie: cięcie laserowe, frezowanie CNC, gięcie, klejenie, druk UV
- Produkty: ekspozytory, kasetony LED, stojaki na ulotki, osłony, organizery
- Branże: kosmetyka, retail, gastronomia, apteki, biura

Zasady odpowiedzi:
1. Bądź konkretny i praktyczny
2. Podawaj orientacyjne ceny gdy to możliwe (plexi ~150 zł/m², dibond ~180 zł/m²)
3. Sugeruj najlepsze rozwiązania dla danego zastosowania
4. Używaj profesjonalnego ale przyjaznego tonu
5. Odpowiadaj po polsku`;

      // Wywołaj Universal AI Service
      const response = await fetch(`${API_URL}/api/universal-ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module_name: 'ai_assistant',
          messages: [
            { role: 'system', content: systemPrompt },
            ...messages.slice(-10).map(msg => ({
              role: msg.role,
              content: msg.content
            })),
            { role: 'user', content }
          ],
          options: {
            max_tokens: 1024,
            temperature: 0.7
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `API Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalizacja odpowiedzi AI - naprawia React Error #31
      let normalizedContent = '';
      if (data.content && Array.isArray(data.content)) {
        // Format Anthropic: {content: [{type: 'text', text: '...'}]}
        const firstContent = data.content[0];
        if (firstContent && typeof firstContent === 'object' && firstContent.type === 'text') {
          normalizedContent = firstContent.text;
        } else {
          normalizedContent = JSON.stringify(data.content);
        }
      } else if (typeof data.content === 'string') {
        normalizedContent = data.content;
      } else if (data.response) {
        normalizedContent = data.response;
      } else if (data.text) {
        normalizedContent = data.text;
      } else {
        normalizedContent = 'Błąd odpowiedzi';
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: normalizedContent,
        timestamp: new Date(),
        tokens: data.usage?.tokens
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update token usage - prawdziwe liczenie
      if (data.usage) {
        const tokensUsed = data.usage.total_tokens || data.usage.tokens || 0;
        
        // Aktualizuj całkowite zużycie
        setTokenUsage(prev => ({
          used: prev.used + tokensUsed,
          limit: prev.limit
        }));
        
        // Aktualizuj dzienne zużycie
        const now = Date.now();
        setDailyUsage(prev => {
          // Reset jeśli minął dzień
          if (now > prev.resetAt) {
            return {
              used: tokensUsed,
              limit: 50000,
              resetAt: new Date().setHours(24,0,0,0)
            };
          }
          return {
            ...prev,
            used: prev.used + tokensUsed
          };
        });
        
        // Zapisz do localStorage dla persistencji
        const usageData = {
          total: tokenUsage.used + tokensUsed,
          daily: dailyUsage.used + tokensUsed,
          lastUpdate: new Date().toISOString()
        };
        localStorage.setItem('plexisystem_token_usage', JSON.stringify(usageData));
      }
      
    } catch (error) {
      console.error('Error calling AI:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Przepraszam, wystąpił problem z połączeniem.

${error instanceof Error && error.message.includes('fetch') ? 'Problem z siecią. Sprawdź połączenie internetowe.' : 'Serwis AI jest chwilowo niedostępny. Spróbuj ponownie za chwilę.'}

Jeśli problem się powtarza, skontaktuj się z administratorem.`,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const copyMessage = (content: string, messageId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const saveConversation = () => {
    const title = prompt('Nazwa konwersacji:') || `Rozmowa ${new Date().toLocaleDateString()}`;
    const category = messages.some(m => m.content.toLowerCase().includes('opis')) ? 'sales' : 'general';
    
    const newConversation: SavedConversation = {
      id: Date.now().toString(),
      title,
      messages: [...messages],
      timestamp: new Date(),
      category
    };
    
    const updated = [...savedConversations, newConversation];
    setSavedConversations(updated);
    localStorage.setItem('plexisystem_ai_conversations', JSON.stringify(updated));
  };

  const loadConversation = (conversation: SavedConversation) => {
    setMessages(conversation.messages);
    setShowHistory(false);
  };

  const deleteConversation = (id: string) => {
    const updated = savedConversations.filter(c => c.id !== id);
    setSavedConversations(updated);
    localStorage.setItem('plexisystem_ai_conversations', JSON.stringify(updated));
  };

  const clearChat = () => {
    setMessages([{
      id: Date.now().toString(),
      role: 'assistant',
      content: 'Witaj ponownie! W czym mogę Ci pomóc?',
      timestamp: new Date()
    }]);
  };

  const categories = [
    { id: 'all', name: 'Wszystkie', icon: <Brain className="w-4 h-4" /> },
    { id: 'sales', name: 'Sprzedaż', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'technical', name: 'Techniczne', icon: <Wrench className="w-4 h-4" /> },
    { id: 'calculator', name: 'Wyceny', icon: <Calculator className="w-4 h-4" /> },
    { id: 'business', name: 'Biznes', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'design', name: 'Projekt', icon: <Palette className="w-4 h-4" /> }
  ];

  const filteredPrompts = selectedCategory === 'all' 
    ? quickPrompts 
    : quickPrompts.filter(p => p.category === selectedCategory);

  return (
    <PageWrapper
      title="AI Assistant"
      subtitle="Inteligentny asystent wspierający Twoją pracę"
    >
      {/* Token Usage Bar */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-sm font-medium text-white">Wykorzystanie tokenów</p>
              <p className="text-xs text-gray-400">Model: Claude 3.5 Sonnet</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{tokenUsage.used.toLocaleString()} / {tokenUsage.limit.toLocaleString()}</p>
              <p className="text-xs text-gray-400">Całkowite ({((tokenUsage.used / tokenUsage.limit) * 100).toFixed(1)}%)</p>
            </div>
            <div className="w-32 bg-gray-700 rounded-full h-2">
              <motion.div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((tokenUsage.used / tokenUsage.limit) * 100, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="border-l border-gray-600 pl-6">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{dailyUsage.used.toLocaleString()} / {dailyUsage.limit.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Dzisiaj ({((dailyUsage.used / dailyUsage.limit) * 100).toFixed(1)}%)</p>
              </div>
            </div>
            <div className="w-32 bg-gray-700 rounded-full h-2">
              <motion.div 
                className={`h-2 rounded-full ${
                  dailyUsage.used > dailyUsage.limit * 0.8 
                    ? 'bg-gradient-to-r from-red-500 to-orange-500' 
                    : 'bg-gradient-to-r from-green-500 to-blue-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((dailyUsage.used / dailyUsage.limit) * 100, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Quick Prompts */}
        <div className="lg:col-span-1 space-y-4">
          {/* Category Filter */}
          <Card className="p-4">
            <SectionTitle>Szybkie prompty</SectionTitle>
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                  }`}
                >
                  {cat.icon}
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Quick Prompts List */}
          <Card className="p-4 max-h-[600px] overflow-y-auto">
            <div className="space-y-2">
              {filteredPrompts.map(prompt => (
                <motion.button
                  key={prompt.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleQuickPrompt(prompt.prompt)}
                  className="w-full text-left p-3 bg-zinc-700/50 hover:bg-zinc-700 rounded-lg transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-zinc-600 rounded-lg group-hover:bg-purple-500 transition-colors">
                      {prompt.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{prompt.title}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{prompt.prompt}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </Card>

          {/* Actions */}
          <Card className="p-4">
            <div className="space-y-2">
              <Button
                onClick={() => setShowHistory(!showHistory)}
                variant="secondary"
                className="w-full justify-start"
              >
                <History className="w-4 h-4" />
                Historia rozmów ({savedConversations.length})
              </Button>
              <Button
                onClick={saveConversation}
                variant="secondary"
                className="w-full justify-start"
                disabled={messages.length <= 1}
              >
                <Download className="w-4 h-4" />
                Zapisz rozmowę
              </Button>
              <Button
                onClick={clearChat}
                variant="secondary"
                className="w-full justify-start"
                disabled={messages.length <= 1}
              >
                <RotateCcw className="w-4 h-4" />
                Nowa rozmowa
              </Button>
            </div>
          </Card>
        </div>

        {/* Main Chat Area */}
        <div className="lg:col-span-3">
          <Card className="h-[800px] flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <AnimatePresence>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`flex gap-3 ${message.role === 'assistant' ? '' : 'justify-end'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                          <Brain className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    )}
                    
                    <div className={`flex-1 max-w-[80%] ${message.role === 'user' ? 'bg-purple-900/30' : 'bg-zinc-700/50'} rounded-lg p-4`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {message.role === 'assistant' ? 'AI Assistant' : 'Ty'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {message.timestamp.toLocaleTimeString()}
                          </span>
                          {message.tokens && (
                            <span className="text-xs text-gray-500">
                              ({message.tokens} tokenów)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => copyMessage(message.content, message.id)}
                          className="p-1 hover:bg-zinc-600 rounded transition-all"
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                      
                      <div className="text-white whitespace-pre-wrap">{message.content}</div>
                      
                      {message.role === 'assistant' && message.content.includes('kalkulator') && (
                        <Button
                          onClick={() => navigate('/calculator')}
                          size="sm"
                          variant="primary"
                          className="mt-3"
                        >
                          <Calculator className="w-4 h-4" />
                          Przejdź do kalkulatora
                        </Button>
                      )}
                    </div>
                    
                    {message.role === 'user' && (
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-zinc-600 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-zinc-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Loader className="w-4 h-4 text-purple-400 animate-spin" />
                        <span className="text-sm text-gray-400">AI myśli...</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-zinc-700 p-4">
              <div className="flex gap-3">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Zadaj pytanie... (Enter = wyślij, Shift+Enter = nowa linia)"
                  className="flex-1 min-h-[60px] max-h-[120px] p-3 bg-zinc-700/50 text-white rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={isLoading}
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!input.trim() || isLoading}
                  variant="primary"
                  className="self-end"
                >
                  {isLoading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  💡 Wskazówka: Wybierz gotowy prompt z lewej strony lub zadaj własne pytanie
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <AlertCircle className="w-3 h-3" />
                  <span>Dane są przetwarzane przez Claude AI</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-zinc-700">
                <h2 className="text-xl font-bold text-white">Historia rozmów</h2>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {savedConversations.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Brak zapisanych rozmów</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedConversations.map(conv => (
                      <div
                        key={conv.id}
                        className="bg-zinc-700/50 rounded-lg p-4 hover:bg-zinc-700 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-white">{conv.title}</h3>
                            <p className="text-sm text-gray-400 mt-1">
                              {conv.messages.length} wiadomości • {conv.timestamp.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => loadConversation(conv)}
                              size="sm"
                              variant="primary"
                            >
                              Wczytaj
                            </Button>
                            <button
                              onClick={() => deleteConversation(conv.id)}
                              className="p-2 hover:bg-red-900/50 text-red-400 rounded-lg transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}
